import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Card,
  Checkbox,
  CardContent,
  Chip,
} from "@mui/material";
import { Close, Done } from "@mui/icons-material";
import { cardBorder } from "services/constants";
import { themes } from "services/constants";
import { CustomTextBox, CustomAutoComplete } from "components/CustomComponents";
import { useSelector } from "react-redux";
import { customAPI } from "services/utils/CustomAPI";
import { CustomButton } from "components/CustomComponents";
import { showToast } from "services/utils/Status";
import { useForm, Controller } from "react-hook-form";
import { minimum3Characters } from "services/constants/ErrorMessages";
import { customGetAPI } from "services/utils/CustomAPI";
import { Autocomplete } from "@material-ui/core";
import { useFetch } from "services/hooks/useFetch";
import { Loader } from "components/Loader";
import * as Icons from "@mui/icons-material";
import sessionHandling from "services/utils/notificationUtils";
import { onlyNumberRegex } from "services/constants/patterns";
import {
  atleastOneCharacterWithSpace,
  specialCharacterRestrict,
} from "services/constants/patterns";
import { useShiftRota } from "../../ShiftRotaContext";

export const EditShiftRotaConfig = forwardRef(({ handleNext, onUpdate }) => {
  const {
    configRef,
    formData,
    WeekDaysData,
    allGroupDetails,
    shiftCycleDetails,
    setNumberOfSft,
    editSftRotaData,
    generateShiftMatrics,
    setIsDateModified,
  } = useShiftRota();
  const {
    register,
    reset,
    handleSubmit,
    setValue,
    watch,
    control,
    trigger,
    clearErrors,
    resetField,
    getValues,
    unregister,
    formState: { errors, dirtyFields },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      ...formData?.config,
      shift: [],
      shiftGroup: [],
      no_of_shifts: 0,
      recallApi: true,
      effective_from: formData?.config?.effective_from || "",
    },
  });

  const AutoSftData = [
    {
      label: "Regular Shift",
    },
    {
      label: "Random",
    },
  ];

  const { domain, token } = useSelector((state) => state.tokenReducer);
  const { result: shiftData } = useFetch(
    `${domain}administration/shiftroster/shift/shifts`
  );
  const {
    result: shiftGrpData,
    setLoad: reCallSftGrpApi,
    loading,
  } = useFetch(`${domain}administration/shiftroster/shiftgroups/shift-groups/`);
  const [openCreate, setOpenCreate] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dateModified = dirtyFields.effective_from;

  useEffect(() => {
    if (dateModified) {
      setIsDateModified(dateModified);
    }
  }, [dateModified, setIsDateModified]);

  const updateShiftAssignmentName = (event) => {
    if (
      specialCharacterRestrict.test(event.target.value) &&
      event.target.value.length <= 64
    ) {
      setValue("shift_assignment_name", event.target.value);
    }
  };

  const updateNumberOfShifs = (event) => {
    if (event.target.value && onlyNumberRegex.test(event.target.value)) {
      setValue("no_of_shifts", event.target.value);
      setValue("shift", []);
      setNumberOfSft(false);
      if (parseInt(event.target.value) === 1) {
        setNumberOfSft(true);
      } else {
        resetField("is_random");
      }
    } else {
      setValue("no_of_shifts", "");
    }
  };

  const updateSftGrpName = (event) => {
    setValue("shift_group_name", event.target.value);
  };

  useEffect(() => {
    if (formData?.config) reset(formData?.config);
  }, [formData?.config, reset]);

  const onSubmit = async (formValues) => {
    onUpdate({
      ...formValues,
      WeekDaysData,
      allGroupDetails,
      shiftCycleDetails,
      data_updated: true,
    });
    handleNext(1);
  };

  useImperativeHandle(configRef, () => ({
    saveFormValues: () => {
      const currentValues = getValues();
      onUpdate({
        ...currentValues,
        WeekDaysData,
        allGroupDetails,
        shiftCycleDetails,
      });
    },
  }));

  const updateGrpName = async (newName) => {
    if (newName) {
      setValue("set_Loading", true);
      let payload = { primary_shift_grade: 1, name: newName.trim() };
      const response = await customAPI(
        `${domain}administration/shiftroster/shiftgroups/shift-groups/`,
        "POST",
        payload
      );
      if (response.ok) {
        showToast("success", "Shift Group Created Successfully");
        reCallSftGrpApi(true);
        setOpenCreate(false);
        clearErrors("shift_group_name");
        unregister("shift_group_name");
        setValue("set_Loading", false);
      }
    }
  };

  const updateIsRandom = (event, data) => {
    if (data) {
      setValue("is_random", data?.label);
    }
  };

  return watch("set_Loading") || loading ? (
    <Loader />
  ) : (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{ flexGrow: 1, overflow: "auto", p: 2 }}
        component="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
          }}
        >
          <Box>
            <Box sx={{ ml: 1 }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                Shift Assignment Name
                <span style={{ color: themes?.redColor }}>*</span>
              </Typography>
              <CustomTextBox
                selectedValue={watch("shift_assignment_name")}
                rules={{
                  ...register("shift_assignment_name", {
                    required: "Shift assignment name is required",
                    validate: {
                      minLengthNoSpaces: (value) => {
                        if (value?.trim().length < 3 && value !== "") {
                          return minimum3Characters;
                        } else {
                          return true;
                        }
                      },
                      atleastOneCharacterWithSpaceReg: (value) => {
                        if (
                          atleastOneCharacterWithSpace.test(value) ||
                          value === ""
                        ) {
                          return true;
                        } else {
                          return "Atleast one alphabet required";
                        }
                      },
                      shiftassignNameAvailable: async (fieldValue) => {
                        if (
                          fieldValue !== "" &&
                          formData?.config?.shift_assignment_name !== fieldValue
                        ) {
                          const res = await customGetAPI(
                            `${domain}administration/shiftroster/shiftrota/unique/shift-assignment/?name=${fieldValue}`
                          );
                          if (res?.responseDetails?.exists) {
                            return "Shift assign name already exists";
                          }
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={updateShiftAssignmentName}
                placeHolder="Enter the shift assignment name"
                errorMessage={
                  errors?.shift_assignment_name?.message
                    ? errors?.shift_assignment_name?.message
                    : ""
                }
                textBoxError={errors.shift_assignment_name ? true : false}
              />
            </Box>
          </Box>
          <Box
            sx={{
              pointerEvents: editSftRotaData?.is_editable ? "auto" : "none",
              opacity: editSftRotaData?.is_editable ? 1 : 0.8,
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
            }}
          >
            <Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 4,
                  ml: 1,
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    No.of Shifts
                    <span style={{ color: themes?.redColor }}>*</span>
                  </Typography>
                  <CustomTextBox
                    selectedValue={watch("no_of_shifts")}
                    rules={{
                      ...register("no_of_shifts", {
                        required: "No of shift is required",
                      }),
                    }}
                    updateDataFunction={updateNumberOfShifs}
                    placeHolder="Enter no of shifts"
                    errorMessage={
                      errors?.no_of_shifts?.message
                        ? errors?.no_of_shifts?.message
                        : ""
                    }
                    textBoxError={errors.no_of_shifts ? true : false}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Effective From
                    <span style={{ color: themes?.redColor }}>*</span>
                  </Typography>
                  <TextField
                    id="from-date"
                    type="date"
                    size="small"
                    {...register("effective_from", {
                      required: "Date is required.",
                    })}
                    error={Boolean(errors.effective_from)}
                    helperText={
                      errors.effective_from?.message &&
                      errors.effective_from?.message
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    fullWidth
                    disableOpenPicker
                  />
                </Box>
              </Box>
            </Box>
            {watch("no_of_shifts") === "1" && (
              <Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 4,
                    ml: 1,
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Shift Type
                      <span style={{ color: themes?.redColor }}>*</span>
                    </Typography>
                    <CustomAutoComplete
                      dataList={AutoSftData?.map((obj) => ({
                        ...obj,
                        label: obj?.label,
                      }))}
                      selectedValue={watch("is_random")}
                      rules={{
                        ...register("is_random", {
                          required: false,
                          validate: {
                            minLengthNoSpaces: (value) => {
                              if (
                                watch("no_of_shifts") === "1" &&
                                !watch("is_random")
                              ) {
                                return "Shift is required";
                              } else {
                                return true;
                              }
                            },
                          },
                        }),
                      }}
                      updateDataFunction={updateIsRandom}
                      placeHolder="Select the rotation frequency"
                      errorMessage={
                        errors?.is_random?.message
                          ? errors?.is_random?.message
                          : ""
                      }
                      textBoxError={errors?.is_random ? true : false}
                    />
                  </Box>
                  <Box></Box>
                </Box>
              </Box>
            )}
            <Box
              sx={{
                borderBottom: `1px solid ${themes?.miliedLightGray}`,
                borderRadius: 0,
              }}
            ></Box>
            <Box
              sx={{
                ml: 1,
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>Shift</Typography>
              <Typography variant="body2" color="text.secondary">
                Choose shifts to include in this rota
              </Typography>
            </Box>
            <Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 4,
                  ml: 1,
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Shift
                    <span style={{ color: themes?.redColor }}>*</span>
                  </Typography>
                  <Controller
                    name="shift"
                    control={control}
                    rules={{
                      required: "Select at least one shift",
                      validate: (value) => {
                        const noOfShifts = watch("no_of_shifts");
                        if (
                          noOfShifts &&
                          value?.length !== parseInt(noOfShifts)
                        ) {
                          return `You must select exactly ${noOfShifts} shift(s)`;
                        }
                        return true;
                      },
                    }}
                    render={({ field }) => (
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        limitTags={1}
                        size="small"
                        options={shiftData || []}
                        getOptionLabel={(option) => option.name}
                        value={field.value || []}
                        onChange={(event, newValue) => {
                          if (newValue.length <= watch("no_of_shifts")) {
                            field.onChange(newValue);
                            generateShiftMatrics(
                              watch("shiftGroup"),
                              watch("shift")
                            );
                          }
                        }}
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        ListboxProps={{
                          style: {
                            maxHeight: "200px",
                            overflowY: "auto",
                          },
                        }}
                        renderOption={(props, option, { selected }) => {
                          const { key, ...optionProps } = props;
                          const isDisabled =
                            field.value?.length >= watch("no_of_shifts") &&
                            !field.value.some((val) => val.id === option.id);
                          return (
                            <li
                              key={key}
                              {...optionProps}
                              style={{
                                opacity: isDisabled ? 0.5 : 1,
                                pointerEvents: isDisabled ? "none" : "auto",
                              }}
                            >
                              <Checkbox
                                icon={
                                  <Icons.CheckBoxOutlineBlank fontSize="small" />
                                }
                                checkedIcon={
                                  <Icons.CheckBox fontSize="small" />
                                }
                                checked={selected}
                                disabled={isDisabled}
                              />
                              {option.name}
                            </li>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            rows={1}
                            maxRows={1}
                            {...params}
                            placeholder={
                              watch("shift").length === 0
                                ? "Select the shift"
                                : ""
                            }
                            error={!!errors.shift}
                            helperText={errors.shift?.message || ""}
                          />
                        )}
                      />
                    )}
                  />
                </Box>
                <Box>
                  <Card sx={cardBorder}>
                    <CardContent>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: themes?.stepActiveColor,
                        }}
                      >
                        Selected Shifts
                      </Typography>

                      {watch("shift")?.length > 0 ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            mt: 1,
                          }}
                        >
                          {watch("shift").map((shift) => (
                            <Chip
                              key={shift.id}
                              label={`${shift.name} (${shift?.start_time} - ${
                                shift?.end_time || "NA"
                              })`}
                              onDelete={() => {
                                const updated = watch("shift").filter(
                                  (item) => item.id !== shift.id
                                );
                                setValue("shift", updated);
                                generateShiftMatrics(
                                  watch("shiftGroup"),
                                  watch("shift")
                                );
                              }}
                              sx={{
                                backgroundColor: shift?.color_code,
                                alignSelf: "flex-start",
                                "& .MuiChip-deleteIcon": {
                                  color: themes?.redColor,
                                },
                              }}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No values selected
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                borderBottom: `1px solid ${themes?.miliedLightGray}`,
                borderRadius: 0,
              }}
            ></Box>
            <Box
              sx={{
                ml: 1,
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>Shift Group</Typography>
              <Typography variant="body2" color="text.secondary">
                Assign shifts under a group for easy management
              </Typography>
            </Box>
            <Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 4,
                  ml: 1,
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Shift Group
                    <span style={{ color: themes?.redColor }}>*</span>
                  </Typography>
                  {!openCreate ? (
                    <Controller
                      name="shiftGroup"
                      control={control}
                      rules={{ required: "Select at least one shift group" }}
                      render={({ field }) => (
                        <Autocomplete
                          multiple
                          disableCloseOnSelect
                          limitTags={1}
                          size="small"
                          options={[
                            ...(shiftGrpData || []),
                            { id: "create", name: "Add New" },
                          ]}
                          open={dropdownOpen}
                          onOpen={() => setDropdownOpen(true)}
                          onClose={() => setDropdownOpen(false)}
                          getOptionLabel={(option) => option.name}
                          value={field.value || []}
                          onChange={(event, newValue, reason) => {
                            const last = newValue[newValue.length - 1];

                            if (last?.id === "create") {
                              newValue.pop();
                              setOpenCreate(true);
                              setDropdownOpen(false);
                            }
                            field.onChange(newValue);
                            generateShiftMatrics(
                              watch("shiftGroup"),
                              watch("shift")
                            );
                          }}
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                          ListboxProps={{
                            style: {
                              maxHeight: "200px",
                              overflowY: "auto",
                            },
                          }}
                          renderOption={(props, option, { selected }) => {
                            const { key, ...optionProps } = props;

                            if (option.id === "create") {
                              return (
                                <li
                                  key="create"
                                  {...optionProps}
                                  style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    textAlign: "center",
                                    color: themes?.blueColor,
                                    fontWeight: 500,
                                    cursor: "pointer",
                                  }}
                                >
                                  {option.name}
                                </li>
                              );
                            }

                            return (
                              <li key={key} {...optionProps}>
                                <Checkbox
                                  icon={
                                    <Icons.CheckBoxOutlineBlank fontSize="small" />
                                  }
                                  checkedIcon={
                                    <Icons.CheckBox fontSize="small" />
                                  }
                                  checked={selected}
                                />
                                {option.name}
                              </li>
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              rows={1}
                              maxRows={1}
                              placeholder={
                                (watch("shiftGroup") || []).length === 0
                                  ? "Select the shift group"
                                  : ""
                              }
                              error={!!errors.shiftGroup}
                              helperText={errors.shiftGroup?.message || ""}
                            />
                          )}
                        />
                      )}
                    />
                  ) : (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <CustomTextBox
                          selectedValue={watch("shift_group_name")}
                          {...register("shift_group_name", {
                            required: "Shift group name is required",
                            minLength: {
                              value: 3,
                              message: "Minimum 3 characters are required",
                            },
                            maxLength: {
                              value: 100,
                              message: "Maximun length is 100 characters",
                            },
                            validate: {
                              atleastOneCharacterWithSpaceReg: (value) => {
                                if (
                                  atleastOneCharacterWithSpace.test(value) ||
                                  value === ""
                                ) {
                                  return true;
                                } else {
                                  return "Atleast one alphabet required";
                                }
                              },
                              editLeaveName: async (fieldValue) => {
                                if (fieldValue.trim() === "") {
                                  return "Minimum 3 characters are required";
                                } else {
                                  try {
                                    const response = await fetch(
                                      `${domain}unique/shift-group/?name=${fieldValue.trim()}`,
                                      {
                                        method: "GET",
                                        headers: {
                                          "content-type": "application/json",
                                          Authorization: `token ${token}`,
                                        },
                                      }
                                    );
                                    const res = await response.json();
                                    if (response.ok) {
                                      if (res.exists) {
                                        return "Shift group is already exists";
                                      } else {
                                        return true;
                                      }
                                    } else if (response.status === 409) {
                                      sessionHandling();
                                    } else {
                                      throw new Error(res.error);
                                    }
                                  } catch (error) {
                                    showToast("error", error.message);
                                  }
                                }
                              },
                            },
                          })}
                          updateDataFunction={updateSftGrpName}
                          placeHolder="Enter shift group name"
                          errorMessage={
                            errors?.shift_group_name?.message
                              ? errors?.shift_group_name?.message
                              : ""
                          }
                          textBoxError={errors.shift_group_name ? true : false}
                        />
                        &nbsp;
                        <IconButton
                          onClick={async () => {
                            const isValid = await trigger("shift_group_name");
                            if (isValid) {
                              updateGrpName(watch("shift_group_name"));
                            }
                          }}
                          sx={{
                            color: themes?.greenColor,
                            backgroundColor: themes?.primary,
                            borderRadius: "50%",
                            "&:hover": { backgroundColor: themes?.primary },
                          }}
                        >
                          <Done />
                        </IconButton>
                        &nbsp;
                        <IconButton
                          onClick={() => {
                            clearErrors("shift_group_name");
                            unregister("shift_group_name");
                            setOpenCreate(false);
                          }}
                          sx={{
                            color: themes?.redColor,
                            backgroundColor: themes?.primary,
                            borderRadius: "50%",
                            "&:hover": { backgroundColor: themes?.primary },
                          }}
                        >
                          <Close />
                        </IconButton>
                      </Box>
                    </>
                  )}
                </Box>
                <Box>
                  <Card sx={cardBorder}>
                    <CardContent>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: themes?.stepActiveColor,
                        }}
                      >
                        Selected Shift Group
                      </Typography>
                      {watch("shiftGroup")?.length > 0 ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            mt: 1,
                          }}
                        >
                          {watch("shiftGroup").map((shift) => (
                            <Chip
                              key={shift.id}
                              label={shift.name}
                              onDelete={() => {
                                const updated = watch("shiftGroup").filter(
                                  (item) => item.id !== shift.id
                                );
                                setValue("shiftGroup", updated);
                                generateShiftMatrics(
                                  watch("shiftGroup"),
                                  watch("shift")
                                );
                              }}
                              sx={{
                                alignSelf: "flex-start",
                                "& .MuiChip-deleteIcon": {
                                  color: themes?.redColor,
                                },
                              }}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No values selected
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              ml: 1,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <CustomButton actionName="Next" typeName="submit" />
          </Box>
        </Box>
      </Box>
    </Box>
  );
});
